import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Mic, Video, Camera, Play, X, Loader2, BookOpen, ChevronDown } from 'lucide-react'
import Layout from '../common/Layout'
import { lifeEventAPI } from '../../config/lifeEventAPI'
import MediaCapture from '../MediaCapture'
import { getMediaServeUrl } from '../../config/mediaAPI'
import { updateWithMedia } from '../../services/mediaService'
import { axiosInstance } from '../../services/api'
import { getChapters } from '../../services/chaptersAPI'

function EditStory() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [story, setStory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [storyType, setStoryType] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [location, setLocation] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false)
  const [localMediaFiles, setLocalMediaFiles] = useState([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState('')
  const [chapters, setChapters] = useState([])
  const [selectedChapter, setSelectedChapter] = useState('')
  const [chaptersLoading, setChaptersLoading] = useState(true)
  const [deletedMediaIds, setDeletedMediaIds] = useState([])
  const [isMediaLoading, setIsMediaLoading] = useState(false)

  const storyTypes = [
    { value: 'family_memory', label: 'Family Memory', icon: '\u2764\uFE0F' },
    { value: 'personal_experience', label: 'Personal Experience', icon: '\uD83C\uDF1F' },
    { value: 'historical_event', label: 'Historical Event', icon: '\uD83D\uDCDA' },
    { value: 'life_lesson', label: 'Life Lesson', icon: '\uD83D\uDCA1' },
    { value: 'funny_moment', label: 'Funny Moment', icon: '\uD83D\uDE04' },
    { value: 'tragic_event', label: 'Tragic Event', icon: '\uD83D\uDC94' },
    { value: 'celebration', label: 'Celebration', icon: '\uD83C\uDF89' },
    { value: 'other', label: 'Other', icon: '\uD83D\uDCDD' }
  ]

  const storyPrompts = [
    "The day Grandma taught me her secret cookie recipe",
    "When your dad got lost on our family vacation",
    "The time I accidentally dyed all our clothes pink",
    "Sunday dinners at my childhood home",
    "How I met your grandmother at a dance",
    "A funny thing that happened at work",
    "My favorite childhood game",
    "The pet that stole our hearts",
    "A tradition we had every summer",
    "The neighbor everyone loved"
  ]

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localMediaFiles.forEach(media => {
        if (media.preview && media.preview.startsWith('blob:')) {
          URL.revokeObjectURL(media.preview)
        }
      })
    }
  }, [])

  // Fetch story data
  useEffect(() => {
    fetchStory()
  }, [id])

  // Fetch chapters
  useEffect(() => {
    const fetchChaptersData = async () => {
      try {
        setChaptersLoading(true)
        const chaptersData = await getChapters()
        setChapters(chaptersData || [])
      } catch (err) {
        console.error('Failed to load chapters:', err)
      } finally {
        setChaptersLoading(false)
      }
    }
    fetchChaptersData()
  }, [])

  const fetchStory = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await lifeEventAPI.getEvent(id)
      setStory(response)

      // Populate form with existing data
      setTitle(response.title || '')
      setContent(response.story || '')
      setSelectedDate(response.date || '')
      setLocation(response.location || '')
      setIsPrivate(response.is_private || false)

      // Map story type from API — try to match existing value
      const apiStoryType = response.event_data?.story_type || ''
      const matchedType = storyTypes.find(t =>
        t.label.toLowerCase() === apiStoryType.toLowerCase() ||
        t.value === apiStoryType
      )
      setStoryType(matchedType ? matchedType.value : 'family_memory')
    } catch (err) {
      console.error('Error fetching story:', err)
      setError('Failed to load story. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const safeRemoveMedia = async (mediaId) => {
    try {
      setIsMediaLoading(true)
      await axiosInstance.post(`/lifeevent/events/${id}/remove_media_file/`, {
        media_file_id: mediaId
      })
      fetchStory()
      return true
    } catch (err) {
      console.error('Error removing media:', err)
      setError('Failed to remove media file')
      return false
    } finally {
      setIsMediaLoading(false)
    }
  }

  const getStoryTypeLabel = (value) => {
    const type = storyTypes.find(t => t.value === value)
    return type ? type.label : 'Family Memory'
  }

  const handleOpenMediaCapture = () => {
    setIsMediaCaptureOpen(true)
  }

  const handleMediaCaptured = (mediaData) => {
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file: mediaData.file,
      preview: mediaData.preview,
      name: mediaData.file.name,
      size: mediaData.file.size,
      type: mediaData.file.type,
      mediaType: mediaData.type,
      duration: mediaData.duration,
      timestamp: mediaData.timestamp
    }
    setLocalMediaFiles(prev => [...prev, localFile])
  }

  const handleCloseMediaCapture = () => {
    setIsMediaCaptureOpen(false)
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    const imageFiles = files.filter(file => file.type.startsWith('image/'))

    if (imageFiles.length === 0) {
      setError('Please drop only image files.')
      return
    }

    imageFiles.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`Image too large: ${file.name}. Maximum size is 10MB.`)
        return
      }
      handleFileUpload(file, 'image')
    })

    if (imageFiles.length > 0) {
      setSuccessMessage(`Added ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} via drag & drop!`)
    }
  }

  const validateFile = (file, mediaType) => {
    const maxSize = 500 * 1024 * 1024
    if (file.size > maxSize) {
      throw new Error('File size exceeds 100MB limit')
    }
    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      video: ['video/mp4', 'video/avi', 'video/quicktime', 'video/wmv', 'video/webm'],
      audio: ['audio/mp3', 'audio/wav', 'audio/m4a', 'audio/aac']
    }
    if (!allowedTypes[mediaType].includes(file.type)) {
      throw new Error(`File type not supported for ${mediaType}. Allowed: ${allowedTypes[mediaType].join(', ')}`)
    }
    return true
  }

  const handleFileUpload = async (file, mediaType) => {
    try {
      validateFile(file, mediaType)
      const previewUrl = URL.createObjectURL(file)
      const localFile = {
        id: `local-${Date.now()}-${Math.random()}`,
        file: file,
        preview: previewUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        mediaType: mediaType
      }
      setLocalMediaFiles(prev => [...prev, localFile])
      setSuccessMessage(`${file.name} added successfully!`)
    } catch (error) {
      console.error('File validation failed:', error)
      setError(`Failed to add ${file.name}: ${error.message}`)
    }
  }

  const handleImageUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif'
    input.multiple = true
    input.onchange = (e) => {
      const files = Array.from(e.target.files)
      files.forEach(file => {
        if (!file.type.startsWith('image/')) {
          setError(`Invalid file type: ${file.name}. Please select an image file.`)
          return
        }
        if (file.size > 10 * 1024 * 1024) {
          setError(`Image too large: ${file.name}. Maximum size is 10MB.`)
          return
        }
        handleFileUpload(file, 'image')
      })
    }
    input.click()
  }

  const handleVideoUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'video/mp4,video/avi,video/quicktime,video/wmv,video/webm,video/x-matroska,.mov,.mkv'
    input.multiple = true
    input.onchange = (e) => {
      const files = Array.from(e.target.files)
      files.forEach(file => {
        if (!file.type.startsWith('video/')) {
          setError(`Invalid file type: ${file.name}. Please select a video file.`)
          return
        }
        if (file.size > 500 * 1024 * 1024) {
          setError(`"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(0)} MB). Videos must be under 500 MB.`)
          return
        }
        handleFileUpload(file, 'video')
      })
    }
    input.click()
  }

  const handleAudioUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg,audio/flac'
    input.multiple = true
    input.onchange = (e) => {
      const files = Array.from(e.target.files)
      files.forEach(file => {
        if (!file.type.startsWith('audio/')) {
          setError(`Invalid file type: ${file.name}. Please select an audio file.`)
          return
        }
        if (file.size > 50 * 1024 * 1024) {
          setError(`Audio file too large: ${file.name}. Maximum size is 50MB.`)
          return
        }
        handleFileUpload(file, 'audio')
      })
    }
    input.click()
  }

  const removeLocalMedia = (fileId) => {
    setLocalMediaFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId)
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview)
      }
      return prev.filter(f => f.id !== fileId)
    })
    setSuccessMessage('Media removed successfully')
  }

  const handleDirectDeleteMedia = async (mediaId) => {
    if (window.confirm('Are you sure you want to delete this media file? This action cannot be undone.')) {
      try {
        const result = await safeRemoveMedia(mediaId)
        if (result) {
          setDeletedMediaIds(prev => [...prev, mediaId])
          setStory(prevStory => ({
            ...prevStory,
            media_files: prevStory.media_files.filter(media => media.id !== mediaId)
          }))
          setSuccessMessage('Media file deleted successfully')
          setTimeout(() => setSuccessMessage(''), 3000)
          setTimeout(async () => { await fetchStory() }, 2000)
        } else {
          setError('Failed to delete media file. Please try again.')
        }
      } catch (error) {
        console.error('Error deleting media:', error)
        setError('Failed to delete media file. Please try again.')
      }
    }
  }

  const selectPrompt = (prompt) => {
    setSelectedPrompt(prompt)
    setTitle(prompt)
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Please provide a story title')
      return
    }

    if (!storyType) {
      setError('Please select a story type')
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccessMessage('')

    try {
      const eventData = {
        template: 12,
        title: title.trim(),
        date: selectedDate || new Date().toISOString().split('T')[0],
        location: location.trim() || null,
        event_data: {
          story_type: getStoryTypeLabel(storyType),
          people_involved: "Family"
        },
        story: content.trim() || null,
        story_responses: {
          what_happened: content.trim() || "Story updated",
          who_was_involved: "Family",
          why_important: getStoryTypeLabel(storyType)
        },
        is_private: isPrivate
      }

      const filesToUpload = localMediaFiles.map(localFile => ({
        file: localFile.file,
        name: localFile.name
      }))

      await updateWithMedia(`/lifeevent/events/${id}/`, eventData, filesToUpload)
      setSuccessMessage('Your story has been updated successfully!')
      setDeletedMediaIds([])
      setLocalMediaFiles([])

      setTimeout(() => {
        navigate(`/stories/${id}`)
      }, 2000)

    } catch (error) {
      console.error('Error updating story:', error)
      setError(error.message || 'Failed to update story. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate(`/stories/${id}`)
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to permanently delete this story? This action cannot be undone.')) {
      // Handle story deletion
      alert('Story deleted successfully')
      navigate('/stories')
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#556B2F] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your story...</p>
          </div>
        </div>
      </Layout>
    )
  }

  const existingMedia = story?.media_files || []
  const totalMediaCount = existingMedia.length + localMediaFiles.length

  return (
    <Layout>
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5DC' }}>
        {/* Header */}
        <div style={{ backgroundColor: '#556B2F' }} className="px-6 py-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <Heart className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">Edit Family Story</h1>
                  <p className="text-green-100">
                    {storyType ? (
                      <span className="flex items-center space-x-2">
                        <span>{storyTypes.find(t => t.value === storyType)?.icon}</span>
                        <span>{getStoryTypeLabel(storyType)}</span>
                      </span>
                    ) : (
                      'Update your memory, experience, or moment'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Error and Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg">
              {successMessage}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Main Story Area */}
            <div className="lg:col-span-2 space-y-6">

              {/* Story Title and Date */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <label className="block text-xl font-semibold mb-4" style={{ color: '#333333' }}>
                  What's your story about?
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Example: The day Grandma taught me her secret cookie recipe"
                  className="w-full p-4 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 mb-4"
                  style={{ focusRingColor: '#556B2F' }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-lg font-semibold mb-3" style={{ color: '#333333' }}>
                      When did this happen?
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full p-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ focusRingColor: '#556B2F' }}
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold mb-3" style={{ color: '#333333' }}>
                      Where did it happen?
                    </label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Grandma's Kitchen, Park, etc."
                      className="w-full p-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ focusRingColor: '#556B2F' }}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm text-gray-600">Keep this story private</span>
                  </label>
                </div>

                <div className="mt-2 text-sm text-gray-500">
                  Don't worry if you can't remember the exact date - approximate is perfectly fine!
                </div>
              </div>

              {/* Story Type Selection */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <label className="block text-xl font-semibold mb-4" style={{ color: '#333333' }}>
                  What type of story is this?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {storyTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setStoryType(type.value)}
                      className={`p-4 rounded-lg border-2 transition-all text-center ${
                        storyType === type.value
                          ? 'border-orange-500 shadow-md'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{
                        backgroundColor: storyType === type.value ? '#E65100' : 'white',
                        color: storyType === type.value ? 'white' : '#333333'
                      }}
                    >
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <div className="font-semibold text-sm">{type.label}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-sm text-gray-500">
                  This helps your family understand the context of your story
                </div>
              </div>

              {/* Chapter Selection */}
              {chapters.length > 0 && (
                <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                  <label className="block text-xl font-semibold mb-4" style={{ color: '#333333' }}>
                    <BookOpen className="w-6 h-6 inline-block mr-2 text-green-700" />
                    Add to a Chapter of Your Life
                  </label>
                  <div className="relative">
                    <select
                      value={selectedChapter}
                      onChange={(e) => setSelectedChapter(e.target.value)}
                      className="w-full p-4 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 appearance-none bg-white pr-12"
                      style={{ focusRingColor: '#556B2F' }}
                    >
                      <option value="">Choose a chapter (optional)</option>
                      {chapters.map((chapter) => (
                        <option key={chapter.id} value={chapter.id}>
                          Chapter {chapter.number}: {chapter.title} ({chapter.start_year}-{chapter.end_year || 'Present'})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                  {selectedChapter && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="text-sm text-green-800">
                        This story will appear in your "{chapters.find(c => c.id === selectedChapter)?.title}" chapter
                      </div>
                    </div>
                  )}
                  <div className="mt-3 text-sm text-gray-500">
                    Organize your stories by life chapters to create your memoir
                  </div>
                </div>
              )}

              {/* Recording Options — "How would you like to tell your story?" */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#333333' }}>
                  How would you like to tell your story?
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Media Capture */}
                  <div className="text-center group">
                    <button
                      onClick={handleOpenMediaCapture}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-green-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Video className="w-10 h-10 text-green-600 group-hover:text-green-700 transition-colors" />
                          <Mic className="w-8 h-8 text-green-500 absolute -bottom-1 -right-1 group-hover:text-green-600 transition-colors" />
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Record Media
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Audio or Video
                      </div>
                      <div className="text-xs text-green-600 font-medium">
                        Live Recording
                      </div>
                    </button>
                  </div>

                  {/* Add Photos */}
                  <div className="text-center group">
                    <button
                      onClick={handleImageUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Camera className="w-12 h-12 text-blue-600 group-hover:text-blue-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-blue-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Photos
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Images
                      </div>
                      <div className="text-xs text-blue-600 font-medium">
                        Multiple Formats
                      </div>
                    </button>
                  </div>

                  {/* Add Videos */}
                  <div className="text-center group">
                    <button
                      onClick={handleVideoUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-purple-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Video className="w-12 h-12 text-purple-600 group-hover:text-purple-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-purple-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Videos
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Files
                      </div>
                      <div className="text-xs text-purple-600 font-medium">
                        MP4, AVI, MOV
                      </div>
                    </button>
                  </div>

                  {/* Add Audio Files */}
                  <div className="text-center group">
                    <button
                      onClick={handleAudioUpload}
                      className="w-full p-6 rounded-xl border-2 border-gray-300 hover:border-orange-500 hover:shadow-lg bg-white transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex flex-col items-center">
                        <div className="relative mb-3">
                          <Mic className="w-12 h-12 text-orange-600 group-hover:text-orange-700 transition-colors" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-orange-600 font-bold">+</span>
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-lg mb-2" style={{ color: '#333333' }}>
                        Add Audio
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        Upload Files
                      </div>
                      <div className="text-xs text-orange-600 font-medium">
                        MP3, WAV, M4A
                      </div>
                    </button>
                  </div>
                </div>

                {/* Drag & Drop Area for Photos */}
                <div
                  className={`mt-6 p-6 border-2 border-dashed rounded-xl transition-all duration-200 ${
                    isDragOver
                      ? 'border-blue-400 bg-blue-50 shadow-lg'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    <Camera className={`w-12 h-12 mx-auto mb-3 transition-colors ${
                      isDragOver ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <h4 className={`text-lg font-medium mb-2 transition-colors ${
                      isDragOver ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      {isDragOver ? 'Drop Photos Here!' : 'Drag & Drop Photos Here'}
                    </h4>
                    <p className="text-sm text-gray-500 mb-3">
                      Or click the "Add Photos" button above to browse files
                    </p>
                    <div className="text-xs text-gray-400">
                      Supports: JPEG, PNG, GIF, WebP, HEIC | Max: 10MB per image
                    </div>
                  </div>
                </div>

                {/* Text Story Option */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <label className="block text-lg font-semibold mb-3" style={{ color: '#333333' }}>
                    Or write your story here:
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Tell your story in your own words... What happened? How did you feel? What made this moment special? Include the little details that bring the memory to life."
                    rows={8}
                    className="w-full p-4 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                    style={{ focusRingColor: '#556B2F' }}
                  />
                  <div className="mt-2 text-sm text-gray-500 flex items-center justify-between">
                    <span>Tip: Write like you're talking to family around the dinner table</span>
                    <span>{content.length} characters</span>
                  </div>
                </div>
              </div>

              {/* Existing Media Attachments */}
              {existingMedia.length > 0 && (
                <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#333333' }}>
                    Current Media ({existingMedia.length}):
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                    {existingMedia.map((media) => (
                      <div key={media.id} className="relative group">
                        <div className="relative">
                          {media.media_type === 'image' ? (
                            <img
                              src={getMediaServeUrl(media.id)}
                              alt={media.title || `Photo`}
                              className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            />
                          ) : media.media_type === 'video' ? (
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                              <video
                                src={`${media.file_url_debug || media.file}#t=0.5`}
                                className="w-full h-full object-cover rounded-lg"
                                muted
                                playsInline
                                preload="auto"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="w-8 h-8 text-white drop-shadow-lg" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <Mic className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                            {(media.media_type || 'file').toUpperCase()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDirectDeleteMedia(media.id)}
                          disabled={isMediaLoading}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Local Media Files */}
              {localMediaFiles.length > 0 && (
                <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#333333' }}>
                    New Media to Upload ({localMediaFiles.length}):
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                    {localMediaFiles.map((media) => (
                      <div key={media.id} className="relative group">
                        <div className="relative">
                          {media.mediaType === 'image' ? (
                            <img
                              src={media.preview}
                              alt={media.name}
                              className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            />
                          ) : media.mediaType === 'video' ? (
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                              <video
                                src={media.preview}
                                className="w-full h-full object-cover rounded-lg"
                                muted
                                preload="metadata"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="w-8 h-8 text-white drop-shadow-lg" />
                              </div>
                              {media.duration && (
                                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                  {formatTime(media.duration)}
                                </div>
                              )}
                            </div>
                          ) : media.mediaType === 'audio' ? (
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                              <Mic className="w-8 h-8 text-gray-400" />
                              {media.duration && (
                                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                  {formatTime(media.duration)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <Mic className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                            {media.mediaType.toUpperCase()}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 truncate">{media.name}</div>
                        <div className="text-xs text-gray-400">{formatFileSize(media.size)}</div>
                        <button
                          onClick={() => removeLocalMedia(media.id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save / Delete Buttons */}
              <div className="text-center space-y-4">
                <button
                  onClick={handleSave}
                  disabled={isSubmitting || !title || !storyType}
                  className={`px-8 py-4 rounded-xl font-bold text-xl shadow-lg transition-all ${
                    isSubmitting || !title || !storyType
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-xl transform hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: '#E65100',
                    color: 'white'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-6 h-6 inline mr-3 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Heart className="w-6 h-6 inline mr-3" />
                      Save Changes
                    </>
                  )}
                </button>

                {totalMediaCount > 0 ? (
                  <div className="text-sm text-gray-600">
                    {existingMedia.length > 0 && `${existingMedia.length} existing media`}
                    {existingMedia.length > 0 && localMediaFiles.length > 0 && ' + '}
                    {localMediaFiles.length > 0 && `${localMediaFiles.length} new file${localMediaFiles.length !== 1 ? 's' : ''} to upload`}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Add photos, videos, or audio to make your story more engaging
                  </div>
                )}

                <div className="flex items-center justify-center space-x-4 pt-2">
                  <button
                    onClick={handleBack}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-6 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Delete Story
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">

              {/* Story Ideas */}
              <div style={{ backgroundColor: '#F2F2F2' }} className="p-6 rounded-xl border border-gray-300 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#333333' }}>
                  Story Ideas
                </h3>
                <div className="space-y-2">
                  {storyPrompts.map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => selectPrompt(prompt)}
                      className={`w-full p-3 text-left text-sm rounded-lg border transition-all ${
                        selectedPrompt === prompt
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                      style={{ color: '#333333' }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recording Tips */}
              <div style={{ backgroundColor: '#FEF3C7' }} className="p-4 rounded-lg border border-yellow-200">
                <h4 className="font-semibold mb-2" style={{ color: '#92400E' }}>
                  Recording Tips
                </h4>
                <ul className="text-sm space-y-1" style={{ color: '#92400E' }}>
                  <li>Find a quiet spot</li>
                  <li>Speak naturally, like talking to family</li>
                  <li>Don't worry about being perfect</li>
                  <li>Include how you felt</li>
                  <li>Mention specific details</li>
                </ul>
              </div>

              {/* What Makes a Great Story */}
              <div style={{ backgroundColor: '#E8F5E8' }} className="p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold mb-2" style={{ color: '#2D5016' }}>
                  Great Stories Include:
                </h4>
                <ul className="text-sm space-y-1" style={{ color: '#2D5016' }}>
                  <li>What you saw, heard, smelled</li>
                  <li>What people said</li>
                  <li>How it made you feel</li>
                  <li>Why it was special</li>
                  <li>The little funny details</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MediaCapture Modal */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={handleCloseMediaCapture}
        onMediaCaptured={handleMediaCaptured}
      />
    </Layout>
  )
}

export default EditStory
